import React from 'react';
import { motion } from 'framer-motion';
import { Shield, Lock, CreditCard, BookOpen, Zap, Star } from 'lucide-react';
import { CTAButton } from './Shared';
import { FeatherQuill, Flame, MoonPhase, LotusBud } from './HealingIcons';
const heroImage = {
  src: 'https://cdn.magicpatterns.com/uploads/qXZyxzfBnr4yExNUEZbB7i/MOCKUP_LANDING.png',
  alt: 'Kit Saberes Ancestrales Argentinos - Sistema clínico completo'
};
export const Hero = () => {
  return <div className="relative overflow-hidden pb-8 md:pt-20 md:pb-12 bg-transparent pt-[10px]" data-id="element-147">
      {/* Background Glow */}
      <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[800px] h-[800px] bg-brand-purpleLight/10 rounded-full blur-[140px] -z-10 pointer-events-none" data-id="element-148"></div>

      {/* Floating Decor */}
      <FeatherQuill className="hidden md:block absolute top-32 left-[6%] w-8 h-8 opacity-30 animate-float-soft -z-10" color="#2D5F3F" style={{
      transform: 'rotate(20deg)'
    }} data-id="element-149" />
      <MoonPhase className="hidden md:block absolute top-48 right-[8%] w-10 h-10 opacity-30 animate-float-soft -z-10" color="#6B9A7A" style={{
      transform: 'rotate(-25deg)',
      animationDelay: '1.2s'
    }} data-id="element-150" />
      <LotusBud className="hidden md:block absolute bottom-40 left-[4%] w-12 h-12 opacity-40 animate-float-soft -z-10" color="#C9A961" style={{
      animationDelay: '2s'
    }} data-id="element-151" />
      <Flame className="hidden md:block absolute bottom-32 right-[5%] w-8 h-8 opacity-50 animate-float-soft animate-ember -z-10" color="#A88838" style={{
      transform: 'rotate(15deg)',
      animationDelay: '0.6s'
    }} data-id="element-152" />

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center" data-id="element-153">
        <motion.div initial={{
        opacity: 0,
        y: 20
      }} animate={{
        opacity: 1,
        y: 0
      }} transition={{
        duration: 0.6
      }} className="bg-transparent space-y-[0px]" data-id="element-154">
          <div className="inline-flex items-center justify-center bg-white/80 backdrop-blur-sm border border-brand-purple/30 px-4 py-1.5 rounded-full mb-6 gap-2 shadow-sm pt-[2px] pb-[2px] pl-[10px] pr-[10px]" data-id="element-155">
            <span className="text-sm" data-id="element-156">🌿</span>
            <span className="font-serif font-semibold tracking-wider text-xs md:text-sm text-brand-purpleDark" data-id="element-157">
              LUMEN AKASHA™
            </span>
          </div>

          <div className="relative inline-block mb-4 max-w-4xl mx-auto" data-id="element-158">
            {/* Ambient glow behind headline */}
            <div className="absolute inset-0 -z-10 blur-3xl opacity-60 pointer-events-none" data-id="element-159">
              <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[110%] h-[140%] bg-gradient-to-r from-brand-purpleLight/30 via-brand-gold/20 to-brand-purple/30 rounded-full" data-id="element-160" />
            </div>

            {/* Sparkles */}
            <span className="sparkle absolute -top-2 left-[8%] text-brand-purple text-sm md:text-xl" style={{
            animationDelay: '0s'
          }} data-id="element-161">
              ✦
            </span>
            <span className="sparkle absolute top-1 right-[6%] text-brand-gold text-xs md:text-lg" style={{
            animationDelay: '0.8s'
          }} data-id="element-162">
              ✧
            </span>
            <span className="sparkle absolute -bottom-1 left-[20%] text-brand-purpleLight text-xs md:text-base" style={{
            animationDelay: '1.6s'
          }} data-id="element-163">
              ✦
            </span>
            <span className="sparkle absolute bottom-2 right-[18%] text-brand-purple text-sm md:text-lg" style={{
            animationDelay: '2.2s'
          }} data-id="element-164">
              ✧
            </span>

            <h1 className="sm:text-4xl md:text-6xl lg:text-7xl text-[28px] font-[700] leading-tight tracking-tight" style={{
            fontFamily: "'Cinzel', serif"
          }} data-id="element-165">
              <span className="headline-shine" data-id="element-166">Tratamientos más efectivos</span>
              <br data-id="element-167" />
              <span className="phrase-glow font-[800]" data-id="element-168">a partir de hoy</span>
              <span className="text-brand-purpleDark" data-id="element-169">.</span>
            </h1>
          </div>
          <p className="text-[18px] md:text-2xl lg:text-3xl text-brand-amber mb-8 font-bold font-serif" data-id="element-170">
            El conocimiento ya lo tenés. El sistema para aplicarlo, ahora
            también.
          </p>

          {/* Hero Image */}
          <div className="relative z-10 w-full max-w-[280px] sm:max-w-sm md:max-w-lg lg:max-w-xl xl:max-w-2xl mx-auto mb-2 flex justify-center" data-id="element-171">
            <div className="relative overflow-hidden rounded-2xl bg-transparent w-full mx-auto" style={{
            aspectRatio: '1/1',
            maxHeight: '500px'
          }} data-id="element-172">
              <motion.img src={heroImage.src} alt={heroImage.alt} initial={{
              opacity: 0,
              scale: 0.95
            }} animate={{
              opacity: 1,
              scale: 1
            }} transition={{
              duration: 0.6,
              ease: 'easeOut'
            }} className="absolute inset-0 w-full h-full object-contain mx-auto drop-shadow-2xl" draggable={false} data-id="element-173" />
            </div>
          </div>

          <div className="max-w-3xl mx-auto mb-8 mt-8 space-y-3 px-2" data-id="element-174">
            <p className="text-[13px] md:text-xl lg:text-2xl font-bold text-brand-purple font-serif leading-relaxed" data-id="element-175">
              El recurso N°1 para terapeutas que trabajan con hierbas nativas
              argentinas — Todos los niveles y cuadros clínicos
            </p>
            <p className="text-[11px] md:text-base lg:text-lg text-brand-text font-sans" data-id="element-176">
              Enciclopedia A-Z + preparaciones profesionales + protocolos
              clínicos + recetario de fórmulas + fichas de paciente listos para
              usar.
            </p>
          </div>

          <div className="flex flex-nowrap items-center justify-center gap-1.5 md:gap-3 mb-12 text-xs md:text-sm font-medium px-2" data-id="element-177">
            <div className="inline-flex items-center bg-white px-2 md:px-3 py-1.5 md:py-2 rounded-full border border-brand-purple/25 whitespace-nowrap shadow-sm text-[9px] md:text-sm" data-id="element-178">
              <BookOpen className="w-3 h-3 md:w-4 md:h-4 mr-1 md:mr-1.5 text-brand-purple shrink-0" data-id="element-179" />
              100% PDF
            </div>
            <div className="inline-flex items-center bg-white px-2 md:px-3 py-1.5 md:py-2 rounded-full border border-brand-purple/25 whitespace-nowrap shadow-sm text-[9px] md:text-sm" data-id="element-180">
              <Zap className="w-3 h-3 md:w-4 md:h-4 mr-1 md:mr-1.5 text-brand-purple shrink-0" data-id="element-181" />
              Acceso en 5 min
            </div>
            <div className="inline-flex items-center bg-white px-2 md:px-3 py-1.5 md:py-2 rounded-full border border-brand-purple/25 whitespace-nowrap shadow-sm text-[9px] md:text-sm" data-id="element-182">
              <span className="mr-1 md:mr-1.5 text-brand-purple shrink-0" data-id="element-183">
                🌿
              </span>
              Todos los niveles
            </div>
          </div>

          <div className="flex flex-col items-center space-y-6" data-id="element-184">
            <CTAButton variant="purple" href="#pricing" className="text-base md:text-xl px-6 md:px-10 py-4 md:py-5 cta-shimmer relative overflow-hidden" data-id="element-185">
              <span className="relative z-10" data-id="element-186">🌿 → QUIERO MI KIT AHORA</span>
            </CTAButton>

            <div className="flex flex-wrap justify-center items-center gap-x-4 gap-y-2 text-xs md:text-sm text-brand-muted" data-id="element-187">
              <div className="flex items-center" data-id="element-188">
                <Shield className="w-3.5 h-3.5 md:w-4 md:h-4 mr-1.5 text-brand-purple" data-id="element-189" />{' '}
                Garantía 7 días
              </div>
              <div className="flex items-center" data-id="element-190">📥 Descarga inmediata</div>
              <div className="flex items-center" data-id="element-191">
                🖨️ Listo para imprimir o usar en tablet
              </div>
              <div className="flex items-center" data-id="element-192">
                <Lock className="w-3.5 h-3.5 md:w-4 md:h-4 mr-1.5 text-brand-purple" data-id="element-193" />{' '}
                Pago seguro
              </div>
              <div className="flex items-center" data-id="element-194">
                <CreditCard className="w-3.5 h-3.5 md:w-4 md:h-4 mr-1.5 text-brand-purple" data-id="element-195" />{' '}
                Mercado Pago
              </div>
            </div>
          </div>
        </motion.div>
      </div>

      {/* Social Proof Bar */}
      <div className="mt-12 border-y border-brand-purple/15 bg-white/60 py-4 backdrop-blur-sm" data-id="element-196">
        <div className="md:hidden flex flex-wrap justify-center gap-x-5 gap-y-2 px-4 text-sm pl-[16px] pr-[16px]" data-id="element-197">
          <div className="flex items-center" data-id="element-198">
            <span className="font-bold text-brand-purple mr-1.5" data-id="element-199">+300</span>{' '}
            terapeutas equipados
          </div>
          <div className="flex items-center" data-id="element-200">
            <span className="font-bold text-brand-purple mr-1.5" data-id="element-201">3</span> Tomos
            principales
          </div>
          <div className="flex items-center" data-id="element-202">
            <span className="font-bold text-brand-purple mr-1.5" data-id="element-203">3</span> Bonus
            incluidos
          </div>
          <div className="flex items-center" data-id="element-204">
            <Star className="w-3.5 h-3.5 text-brand-gold fill-brand-gold mr-1" data-id="element-205" />{' '}
            4.9/5
          </div>
        </div>

        <div className="hidden md:flex flex-row items-center gap-6 justify-center whitespace-nowrap px-4 text-sm lg:text-base" data-id="element-206">
          <div className="flex items-center" data-id="element-207">
            <span className="font-bold text-brand-purple mr-2" data-id="element-208">+300</span>{' '}
            terapeutas equipados
          </div>
          <div className="w-1.5 h-1.5 rounded-full bg-brand-purpleLight/50 shrink-0" data-id="element-209"></div>
          <div className="flex items-center" data-id="element-210">
            <span className="font-bold text-brand-purple mr-2" data-id="element-211">3</span> Tomos
            principales
          </div>
          <div className="w-1.5 h-1.5 rounded-full bg-brand-purpleLight/50 shrink-0" data-id="element-212"></div>
          <div className="flex items-center" data-id="element-213">
            <span className="font-bold text-brand-purple mr-2" data-id="element-214">3</span> Bonus
            incluidos
          </div>
          <div className="w-1.5 h-1.5 rounded-full bg-brand-purpleLight/50 shrink-0" data-id="element-215"></div>
          <div className="flex items-center" data-id="element-216">
            <Star className="w-4 h-4 text-brand-gold fill-brand-gold mr-1" data-id="element-217" />{' '}
            4.9/5 calificación
          </div>
        </div>
      </div>
    </div>;
};